import { useMemo } from "react";
import { useResource } from "../lib/hooks";
import { api } from "../lib/api";
import { InitialsAvatar } from "./InitialsAvatar";
import { useAuth } from "../context/AuthContext";

/**
 * A provider's own org chart, built from how THEY register their people:
 * contacts of the company, positioned by job_title, wired by reports_to.
 * Managers can set each person's reporting line right here; the tree reflows
 * live. Reuses the main org-chart card styling.
 */

interface ContactRow {
    id: string; first_name: string; last_name: string;
    job_title: string | null; company_id: string | null; reports_to: string | null;
}

interface Node { contact: ContactRow; reports: Node[]; }

const buildTree = (contacts: ContactRow[]): Node[] => {
    const byManager = new Map<string | null, ContactRow[]>();
    for (const c of contacts) {
        const key = c.reports_to ?? null;
        byManager.set(key, [...(byManager.get(key) ?? []), c]);
    }
    const grow = (c: ContactRow): Node => ({
        contact: c,
        reports: (byManager.get(c.id) ?? []).map(grow),
    });
    const ids = new Set(contacts.map((c) => c.id));
    return contacts.filter((c) => !c.reports_to || !ids.has(c.reports_to)).map(grow);
};

export function ProviderOrgChart({ companyId }: { companyId: string }) {
    const { user } = useAuth();
    // Partners manage their own people (RLS scopes them to their company);
    // TrustCare admins/managers can tidy any provider's chart.
    const canEdit = user?.role === "admin" || user?.role === "manager" || user?.role === "partner";
    const contacts = useResource<ContactRow[]>(`/contacts?company_id=${companyId}`);

    const rows = useMemo(() => contacts.data ?? [], [contacts.data]);
    const roots = useMemo(() => buildTree(rows), [rows]);
    const fullName = (c: ContactRow) => `${c.first_name} ${c.last_name}`;

    const setReportsTo = async (c: ContactRow, reportsTo: string) => {
        await api.put(`/contacts/${c.id}`, {
            first_name: c.first_name, last_name: c.last_name,
            company_id: c.company_id, reports_to: reportsTo || null,
        });
        contacts.reload();
    };

    const Card = ({ node }: { node: Node }) => (
        <li>
            <div className="org-card" style={{ cursor: "default" }}>
                <InitialsAvatar name={fullName(node.contact)} size={34} />
                <div className="org-card-name">{fullName(node.contact)}</div>
                <div className="org-card-role">{node.contact.job_title ?? "—"}</div>
                {canEdit && (
                    <select
                        value={node.contact.reports_to ?? ""}
                        onChange={(e) => void setReportsTo(node.contact, e.target.value)}
                        style={{ fontSize: "0.68rem", marginTop: 4, maxWidth: 140 }}
                        title="Reports to"
                    >
                        <option value="">Reports to — top</option>
                        {rows.filter((r) => r.id !== node.contact.id).map((r) => (
                            <option key={r.id} value={r.id}>{fullName(r)}</option>
                        ))}
                    </select>
                )}
            </div>
            {node.reports.length > 0 && (
                <ul>{node.reports.map((r) => <Card key={r.contact.id} node={r} />)}</ul>
            )}
        </li>
    );

    if (!rows.length) {
        return <p className="empty">No people registered for this provider yet — add contacts from the Companies page.</p>;
    }
    return (
        <div className="org-scroll">
            <div className="org-tree">
                <ul>{roots.map((r) => <Card key={r.contact.id} node={r} />)}</ul>
            </div>
        </div>
    );
}
